<template>
  <main class="cai-hero" @click="closeChangelog">
    <button
      class="cai-version-badge"
      type="button"
      @click.stop="toggleChangelog"
    >
      {{ appVersion }} · What's new
      <cai-icon
        name="star"
        :size="12"
      />
    </button>
    <div
      v-if="showChangelog"
      class="cai-changelog"
      @click.stop
    >
      <div class="cai-changelog-title">What's new</div>
      <div
        v-for="f in recentFeatures"
        :key="f.title"
        class="cai-changelog-item"
      >
        <div class="cai-changelog-item-title">{{ f.title }}</div>
        <div class="cai-changelog-item-desc">{{ f.desc }}</div>
      </div>
    </div>
    <div class="cai-hero-inner">
      <h1 class="cai-hero-title">
        {{ content.title }}
      </h1>

      <p class="cai-hero-lead">
        {{ content.lead }}
      </p>
      <p class="cai-hero-desc">
        {{ content.desc }}
      </p>

      <!-- Example cards, "More examples" and chips removed (Oct 2026) for a clean start screen.
           Their content (cards / chips below) is kept so they can be restored. -->

      <div
        v-if="uploading || attachError || attachment"
        class="chat-attachment-chip"
        :class="{ 'chat-attachment-chip--error': attachError }"
      >
        <span v-if="uploading">Reading {{ pendingName }}…</span>
        <span v-else-if="attachError">⚠️ {{ attachError }}</span>
        <span v-else>📎 {{ attachment.name }}<template v-if="attachment.truncated"> (truncated)</template></span>
        <button
          v-if="!uploading"
          class="chat-attachment-remove"
          aria-label="Remove attachment"
          @click="clearAttachment"
        >
          ✕
        </button>
      </div>

      <div class="cai-input">
        <button
          class="cai-input-clip"
          type="button"
          aria-label="Attach a file"
          title="Attach a file (PDF, Word, PowerPoint, Markdown, text, CSV or Excel)"
          :disabled="uploading"
          @click="triggerFilePicker"
        >
          <cai-icon
            name="paperclip"
            :size="15"
          />
        </button>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          accept=".pdf,.docx,.pptx,.md,.txt,.csv,.xlsx"
          @change="onFileChange"
        >
        <input
          ref="input"
          v-model="draft"
          type="text"
          :placeholder="content.placeholder"
          class="cai-input-field"
          @keydown.enter.prevent="submit"
        >
        <button
          class="cai-send"
          aria-label="Send"
          :disabled="uploading"
          @click="submit"
        >
          <cai-icon
            name="send"
            :size="14"
          />
        </button>
      </div>

      <div class="cai-facts">
        <div
          v-for="f in content.facts"
          :key="f.label"
          class="cai-fact"
        >
          <cai-icon
            :name="f.icon"
            :size="15"
          />
          <span>{{ f.label }}</span>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import CaiIcon from './CaiIcon.vue'
import { uploadChatAttachment } from '../api'

// Landing content per analysis environment. Each card's `message` is the prompt
// actually sent — the deck's wording on the outside, a concrete question on the
// inside, so a click lands on something the agent can answer.
const GENERAL = {
  icon: 'brain',
  title: 'CAI',
  lead: 'Deeper insights into people, teams and organisations.',
  desc: "Analyse psychological profiles, compare individuals and understand patterns in teams. CAI is based on Capacio's cognitive (executive) measures and selected self-reported psychological measures, and can also interpret other evidence-based psychological test results.",
  placeholder: 'Ask CAI about profiles, teams, roles or organisations …',
  cards: [
    {
      icon: 'user',
      title: 'What is a psychological profile?',
      sub: 'Get a short and clear explanation.',
      message: "What is a psychological profile in Capacio's model, and what does it consist of?"
    },
    {
      icon: 'brain',
      title: 'What are executive functions?',
      sub: 'Understand the foundation of our analyses.',
      message: 'What are executive functions, and why do they matter for performance?'
    },
    {
      icon: 'page-search',
      title: "Analyse Player 2's profile",
      sub: 'Get a summary and interpretation.',
      message: "Show Player 2's cognitive profile, and walk me through how to read and interpret it, step by step."
    },
    {
      icon: 'users',
      title: 'Compare Player 2 and Player 3',
      sub: 'See similarities, differences and complementary strengths.',
      message: "Compare Player 2 and Player 3's profiles — what are the similarities, differences and complementary strengths?"
    },
    {
      icon: 'team',
      title: 'Analyse a team',
      sub: 'Identify patterns and development areas.',
      message: "What patterns stand out across the group's cognitive profiles — where is the group strongest, and where does it vary most?"
    },
    {
      icon: 'stats-report',
      title: "Suggest Player 4's development plan",
      sub: 'Get concrete and evidence-based suggestions.',
      message: "How do you turn Player 4's cognitive profile into a concrete development plan?"
    }
  ],
  chips: [
    { label: 'Who is CAI?', message: 'Who are you, and what can you help me with?' },
    { label: 'What measures are used?', message: 'Which measures does Capacio use, and what does each one capture?' },
    { label: 'How do we interpret results?', message: 'How should I read a stanine score from 1 to 9?' },
    { label: 'Limitations and ethics', message: 'What are the limitations of these measures, and what should I be careful about ethically?' }
  ],
  facts: [
    { icon: 'shield', label: 'Built on evidence' },
    { icon: 'brain', label: 'Cognitive (executive) and other psychological measures at the core' },
    { icon: 'test', label: 'Can interpret other psychological test results' },
    { icon: 'team', label: 'For individuals, teams and organisations' }
  ]
}

// Sport environments share a shape; only the vocabulary and the example
// players change, so they are built from one template.
function sportContent ({ icon, sport, title, lead, prompts }) {
  const lower = sport.toLowerCase()
  return {
    icon,
    title,
    lead,
    desc: `Analyse psychological profiles, compare players and understand patterns across squads and roles. ${title} is based on Capacio's cognitive (executive) measures and selected self-reported psychological measures, and adds ${lower}-specific roles, situations and performance context.`,
    placeholder: `Ask ${title} about players, roles, situations or squads …`,
    cards: [
      {
        icon: 'pitch',
        title: prompts.intro.title,
        sub: prompts.intro.sub,
        message: prompts.intro.message
      },
      {
        icon: 'brain',
        title: `What are executive functions in ${lower}?`,
        sub: `Understand the cognitive foundation of our ${lower} analyses.`,
        message: `Which executive functions matter most in ${lower}, and why?`
      },
      {
        icon: 'page-search',
        title: `Analyse Player ${prompts.profilePlayer}'s profile`,
        sub: 'Get a summary and interpretation.',
        message: prompts.profile
      },
      {
        icon: 'users',
        title: `Compare Player ${prompts.comparePlayers[0]} and Player ${prompts.comparePlayers[1]}`,
        sub: 'See similarities, differences and complementary strengths.',
        message: prompts.compare
      },
      {
        icon: 'team',
        title: 'Analyse a team or squad',
        sub: 'Identify patterns, role fit and development areas.',
        message: prompts.team
      },
      {
        icon: 'stats-report',
        title: `Suggest Player ${prompts.planPlayer}'s development plan`,
        sub: 'Get concrete and evidence-aware suggestions.',
        message: prompts.plan
      }
    ],
    chips: prompts.more,
    facts: [
      { icon: 'shield', label: 'Built on evidence' },
      { icon: 'brain', label: 'Cognitive (executive) and self-reported psychological measures at the core' },
      { icon: 'pitch', label: `Adds ${lower} roles, situations and performance context` },
      { icon: 'team', label: 'For players, squads and staff' }
    ]
  }
}

const FOOTBALL = sportContent({
  icon: 'football',
  sport: 'Football',
  title: 'CAI Football',
  lead: 'Deeper insights into football players, teams and game contexts.',
  prompts: {
    intro: {
      title: 'Working memory for a centre-back',
      sub: 'See what one ability means in a real position.',
      message: 'What does working memory mean for a centre-back?'
    },
    profilePlayer: 2,
    profile: "Show Player 2's cognitive profile with a spider chart, and highlight the standout strengths and risks.",
    comparePlayers: [2, 3],
    compare: 'Compare Player 2 and Player 3 across all measures.',
    team: 'Who are the top 3 players for strategic thinking, and what does that mean for the squad?',
    planPlayer: 4,
    plan: "Build a development plan for Player 4's weakest executive abilities.",
    more: [
      { label: 'How does Player 1 fit a centre-back role?', message: "Which football position best fits Player 1's executive profile, and why?" },
      { label: 'Compare Player 1, Player 2 and Player 3', message: 'Compare Player 1, Player 2 and Player 3 — strengths, challenges and how they complement each other.' },
      { label: 'How to coach and talk to Player 5', message: 'How should a coach communicate with Player 5 based on their cognitive profile?' },
      { label: 'Which abilities matter under high press?', message: 'Which executive abilities matter most for a player under a high press, and how do I see them in a profile?' }
    ]
  }
})

const HANDBALL = sportContent({
  icon: 'handball',
  sport: 'Handball',
  title: 'CAI Handball',
  lead: 'Deeper insights into handball players, teams and game contexts.',
  prompts: {
    intro: {
      title: 'Impulse control: shoot or pass?',
      sub: 'See what one ability means in a real game decision.',
      message: 'What does impulse control mean when a back player decides between shooting and passing?'
    },
    profilePlayer: 2,
    profile: "Show Player 2's cognitive profile with a spider chart, and highlight the standout strengths and risks.",
    comparePlayers: [1, 2],
    compare: 'Compare Player 1 and Player 2 across all measures.',
    team: 'Who are the top 3 players for emotional regulation, and what does that mean for the squad?',
    planPlayer: 5,
    plan: "Build a development plan for Player 5's weakest executive abilities.",
    more: [
      { label: 'Which position fits Player 3?', message: "Which handball position best fits Player 3's executive profile, and why?" },
      { label: 'What does a goalkeeper need?', message: 'Which executive abilities matter most for a handball goalkeeper?' },
      { label: 'How to coach and talk to Player 4', message: 'How should a coach communicate with Player 4 based on their cognitive profile?' },
      { label: 'Reading the game in a 6-0 defence', message: 'Which cognitive abilities support game reading in a 6-0 defence?' }
    ]
  }
})

const CONTENT_BY_CONTEXT = {
  general: GENERAL,
  football: FOOTBALL,
  handball: HANDBALL
}

// Shown as a small badge on the landing page so people can see the app is
// actively evolving, and skim what's new without digging through release
// notes. Bump APP_VERSION and add an entry here whenever something worth
// mentioning ships.
const APP_VERSION = 'v1.3'
const RECENT_FEATURES = [
  { title: 'Attach files to your chat', desc: 'Add a PDF, Word, PowerPoint, spreadsheet or text file to a message.' },
  { title: 'See what CAI is doing', desc: 'A live status line shows when CAI is searching, comparing or calculating — no more plain spinner.' },
  { title: 'Open reports in one click', desc: 'Player reports now open in a clean dedicated view instead of an inline preview.' },
  { title: 'Resizable context panel', desc: 'Drag to resize the player or team panel to fit your screen.' },
  { title: 'Refreshed icons', desc: 'A cleaner, more consistent icon set across the app.' }
]

export default {
  components: { CaiIcon },

  props: {
    context: { type: String, default: '' },
    contextName: { type: String, default: '' }
  },

  data () {
    return {
      draft: '',
      uploading: false,
      pendingName: '',
      attachment: null, // { name, text, truncated } — extracted server-side, this turn only
      attachError: '',
      appVersion: APP_VERSION,
      recentFeatures: RECENT_FEATURES,
      showChangelog: false
    }
  },

  computed: {
    // Any context without its own landing copy (a future sport/org added from
    // the admin dashboard) gets the general one, re-titled with its own name,
    // rather than silently inheriting football's player-numbered prompts.
    content () {
      const known = CONTENT_BY_CONTEXT[this.context]
      if (known) return known
      if (!this.contextName) return GENERAL
      return {
        ...GENERAL,
        title: `CAI ${this.contextName}`,
        placeholder: `Ask CAI ${this.contextName} about profiles, teams or roles …`
      }
    }
  },

  mounted () {
    if (this.$refs.input) this.$refs.input.focus()
  },

  methods: {
    toggleChangelog () {
      this.showChangelog = !this.showChangelog
    },

    closeChangelog () {
      this.showChangelog = false
    },
    triggerFilePicker () {
      this.attachError = ''
      this.$refs.fileInput.click()
    },

    async onFileChange (e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = '' // reset so picking the same file again still fires 'change'
      if (!file) return
      this.attachment = null
      this.attachError = ''
      this.uploading = true
      this.pendingName = file.name
      try {
        const { data } = await uploadChatAttachment(file)
        this.attachment = { name: data.filename, text: data.text, truncated: data.truncated }
      } catch (err) {
        this.attachError = (err.response && err.response.data && err.response.data.detail) || 'Could not read this file.'
      } finally {
        this.uploading = false
      }
    },

    clearAttachment () {
      this.attachment = null
      this.attachError = ''
    },

    submit () {
      if (this.uploading) return
      const message = this.draft.trim() || (this.attachment ? 'Please review the attached file.' : '')
      if (!message) return
      this.draft = ''
      const attachment = this.attachment
      this.attachment = null
      this.$emit('send', message, attachment)
    }
  }
}
</script>
